"use client";

import { useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm, useFieldArray } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import {
  IconUserPlus,
  IconLoader2,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MultiSelect } from "@/components/ui/multi-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { inviteVenueUser } from "@/services/venue";
import { OrganizationalRole, VenueUserRole } from "@/types";
import {
  ORGANIZATIONAL_ROLE_OPTIONS,
  organizationalRolesForVenueRole,
} from "@/features/venue/organizational-roles";

// Schema idioms deliberately match the zod major that is actually installed
// (3.25) rather than the one package.json pins. `errorMap` is dropped because it
// was removed in zod 4 — the role select is always populated, so the enum never
// needs custom copy anyway.
const inviteUserSchema = z.object({
  users: z
    .array(
      z.object({
        email: z.string().email("Please enter a valid email address"),
        firstName: z.string().min(1, "First name is required"),
        lastName: z.string().optional(),
        venueUserRole: z.nativeEnum(VenueUserRole),
        organizationalRoles: z
          .array(z.nativeEnum(OrganizationalRole))
          .min(1, "Select at least one organizational role"),
      })
    )
    .min(1, "At least one user is required"),
});

type InviteUserFormData = z.infer<typeof inviteUserSchema>;

const emptyRow = {
  email: "",
  firstName: "",
  lastName: "",
  venueUserRole: VenueUserRole.MEMBER,
  organizationalRoles: organizationalRolesForVenueRole(VenueUserRole.MEMBER),
};

interface InviteUserDialogProps {
  venueId: string;
  venueName: string;
  onSuccess?: () => void;
  children?: React.ReactNode;
}

export function InviteUserDialog({
  venueId,
  venueName,
  onSuccess,
  children,
}: InviteUserDialogProps) {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { errors },
  } = useForm<InviteUserFormData>({
    resolver: zodResolver(inviteUserSchema),
    defaultValues: {
      users: [{ ...emptyRow }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "users",
  });

  // Rows whose organizational roles were chosen by hand. Changing Role
  // re-derives the field for every other row, but must not undo a deliberate
  // choice. Keyed by the field-array id so removing a row above does not
  // transfer one row's history to another.
  const manuallySetRowsRef = useRef<Set<string>>(new Set());

  const resetForm = () => {
    manuallySetRowsRef.current.clear();
    reset();
  };

  const onSubmit = async (data: InviteUserFormData) => {
    try {
      setIsSubmitting(true);
      const response = await inviteVenueUser(venueId, {
        users: data.users.map((user) => ({
          email: user.email.toLowerCase(),
          firstName: user.firstName,
          lastName: user.lastName || undefined,
          venueUserRole: user.venueUserRole,
          organizationalRoles: user.organizationalRoles,
        })),
      });

      // Show results
      const { successCount, failureCount, results } = response;

      if (failureCount === 0) {
        toast.success(
          response.message || `Successfully sent ${successCount} invitation(s)`
        );
      } else {
        toast.warning(
          `Sent ${successCount} invitation(s), ${failureCount} failed`
        );

        // Show individual errors
        results.forEach((result) => {
          if (!result.success && result.error) {
            toast.error(`${result.email}: ${result.error}`);
          }
        });
      }

      setOpen(false);
      resetForm();
      onSuccess?.();
    } catch (error) {
      console.error("Error inviting user:", error);
      const errorMessage =
        typeof error === "string"
          ? error
          : error instanceof Error
            ? error.message
            : "Failed to send invitation";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!isSubmitting) {
      setOpen(newOpen);
      if (!newOpen) {
        resetForm();
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {children || (
          <Button>
            <IconUserPlus className="mr-2 size-4" />
            Invite User
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-6xl max-h-[90vh] flex flex-col">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col h-full"
        >
          <DialogHeader>
            <DialogTitle>Invite Users to {venueName}</DialogTitle>
            <DialogDescription>
              Send invitations to users to join this venue. They will receive an
              email with instructions to accept.
            </DialogDescription>
          </DialogHeader>

          {/* Column Headers */}
          <div className="grid grid-cols-[1fr_1fr_1fr_1fr_1fr_auto] gap-3 px-4 pt-4">
            <div>
              <Label>
                Email <span className="text-destructive">*</span>
              </Label>
            </div>
            <div>
              <Label>
                First Name <span className="text-destructive">*</span>
              </Label>
            </div>
            <div>
              <Label>Last Name</Label>
            </div>
            <div>
              <Label>
                Role <span className="text-destructive">*</span>
              </Label>
            </div>
            <div>
              {/*
                A column heading, not a field label — it has no `htmlFor` and
                wraps no control, and each row's MultiSelect carries its own
                per-person name. Hidden so it is not announced as a label
                pointing at nothing. (The four headings above have the same
                shape and predate this change.)
              */}
              <Label aria-hidden="true">
                Organizational Role{" "}
                <span className="text-destructive">*</span>
              </Label>
            </div>
            <div className="w-10"></div>
          </div>

          {/* Scrollable User Rows */}
          <div className="flex-1 overflow-y-auto py-4 px-4 space-y-3 max-h-[50vh]">
            {fields.map((field, index) => (
              <div key={field.id} className="rounded-lg border p-4">
                <div className="grid grid-cols-[1fr_1fr_1fr_1fr_1fr_auto] gap-3 items-start">
                  <div className="grid gap-2">
                    <Input
                      id={`users.${index}.email`}
                      type="email"
                      placeholder="user@example.com"
                      {...register(`users.${index}.email`)}
                      disabled={isSubmitting}
                    />
                    {errors.users?.[index]?.email && (
                      <p className="text-sm text-destructive">
                        {errors.users[index]?.email?.message}
                      </p>
                    )}
                  </div>

                  <div className="grid gap-2">
                    <Input
                      id={`users.${index}.firstName`}
                      type="text"
                      placeholder="John"
                      {...register(`users.${index}.firstName`)}
                      disabled={isSubmitting}
                    />
                    {errors.users?.[index]?.firstName && (
                      <p className="text-sm text-destructive">
                        {errors.users[index]?.firstName?.message}
                      </p>
                    )}
                  </div>

                  <div className="grid gap-2">
                    <Input
                      id={`users.${index}.lastName`}
                      type="text"
                      placeholder="Doe"
                      {...register(`users.${index}.lastName`)}
                      disabled={isSubmitting}
                    />
                  </div>

                  <div className="grid gap-2">
                    {/* Controlled through the form library rather than faking a
                        change event through the register callback. */}
                    <Controller
                      control={control}
                      name={`users.${index}.venueUserRole`}
                      render={({ field: roleField }) => (
                        <Select
                          value={roleField.value}
                          onValueChange={(value) => {
                            roleField.onChange(value);
                            const isAdmin = value === VenueUserRole.ADMIN;
                            // Choosing Admin selects both, overwriting a choice
                            // made while the row was a Member. Only a Member
                            // keeps what was picked by hand.
                            if (!isAdmin && manuallySetRowsRef.current.has(field.id))
                              return;
                            if (isAdmin) manuallySetRowsRef.current.delete(field.id);
                            const derivedRoles =
                              organizationalRolesForVenueRole(
                                value as VenueUserRole,
                              );
                            setValue(
                              `users.${index}.organizationalRoles`,
                              derivedRoles,
                              // Validate ONLY when the derivation fills the
                              // field. After a failed submit `reValidateMode`
                              // re-checks just the changed field, so a stale
                              // "select at least one" would otherwise survive
                              // under a field Admin has already populated.
                              // Going the other way derives [], and validating
                              // that would raise the error before the user has
                              // been given a chance to choose.
                              { shouldValidate: derivedRoles.length > 0 },
                            );
                          }}
                          disabled={isSubmitting}
                        >
                          <SelectTrigger
                            aria-label={`Role for person ${index + 1}`}
                          >
                            <SelectValue placeholder="Select a role" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={VenueUserRole.ADMIN}>
                              Venue Admin
                            </SelectItem>
                            <SelectItem value={VenueUserRole.MEMBER}>
                              Venue Member
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    />
                    {errors.users?.[index]?.venueUserRole && (
                      <p className="text-sm text-destructive">
                        {errors.users[index]?.venueUserRole?.message}
                      </p>
                    )}
                  </div>

                  <div className="grid gap-2">
                    <Controller
                      control={control}
                      name={`users.${index}.organizationalRoles`}
                      render={({ field: orgRolesField }) => (
                        <MultiSelect
                          value={orgRolesField.value ?? []}
                          onValueChange={(value) => {
                            manuallySetRowsRef.current.add(field.id);
                            orgRolesField.onChange(value);
                          }}
                          options={ORGANIZATIONAL_ROLE_OPTIONS}
                          placeholder="Select at least one"
                          aria-label={`Organizational role for person ${index + 1}`}
                          aria-describedby={`users-${index}-organizational-roles-error`}
                          invalid={!!errors.users?.[index]?.organizationalRoles}
                          required
                          disabled={isSubmitting}
                        />
                      )}
                    />
                    {errors.users?.[index]?.organizationalRoles && (
                      <p
                        id={`users-${index}-organizational-roles-error`}
                        className="text-sm text-destructive"
                      >
                        {errors.users[index]?.organizationalRoles?.message}
                      </p>
                    )}
                  </div>

                  <div className="flex items-start pt-0">
                    {fields.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-10 w-10"
                        onClick={() => {
                          manuallySetRowsRef.current.delete(field.id);
                          remove(index);
                        }}
                        disabled={isSubmitting}
                      >
                        <IconTrash className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}

            <Button
              type="button"
              variant="outline"
              onClick={() => append({ ...emptyRow })}
              disabled={isSubmitting}
              className="w-full"
            >
              <IconPlus className="mr-2 size-4" />
              Add Another User
            </Button>
          </div>

          {/* Fixed Footer */}
          <DialogFooter className="pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && (
                <IconLoader2 className="mr-2 size-4 animate-spin" />
              )}
              Send Invitation{fields.length > 1 ? "s" : ""}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
